import { useEffect, useRef } from 'react';
import { IMPACT_REPORT_LABEL_ID } from '@/impactReports/impactReport.types';
import { sqContextApi } from '@seeqdev/api/endpoint/ContextApi';
import type { ImpactReportCategory } from '@/impactReports/impactReport.types';

/** Tracks successful category writes independently of the separately published report row. */
export const useImpactReportCategorySave = (
  report: Record<string, string | undefined> | undefined,
  categories: ImpactReportCategory[] = [],
): ((
  tableId: string,
  datumId: string,
  selections: Record<string, string>,
  beforeChanges?: () => Promise<void>,
) => Promise<void>) => {
  const savedLabels = useRef<Record<string, { contextId?: string; labelId?: string } | undefined>>({});

  useEffect(() => {
    if (!report) return;
    for (const { category } of categories) {
      // Late report data seeds only missing entries, preserving IDs from successful writes.
      savedLabels.current[category.id!] ??= {
        contextId: report[`${category.id}_${IMPACT_REPORT_LABEL_ID}`],
        labelId: report[category.id!],
      };
    }
  }, [report, categories]);

  return async (tableId, datumId, selections, beforeChanges): Promise<void> => {
    const changes = Object.entries(selections)
      .filter(([categoryId, labelId]) => savedLabels.current[categoryId]?.labelId !== labelId)
      .map(([categoryId, labelId]) => ({
        contextLabelId: savedLabels.current[categoryId]?.contextId,
        datumId,
        labelId,
      }));
    if (changes.length === 0) return;

    // Invalidate an existing approval before any category mutation can become visible.
    await beforeChanges?.();
    const { data } = await sqContextApi.batchCreateContextLabels(changes, { itemId: tableId });
    // Keep the server-assigned IDs even if publishing the report subsequently fails.
    for (const label of data) {
      savedLabels.current[label.labelCategoryId] = { contextId: label.contextId, labelId: label.context };
    }
  };
};
